<script setup>
import { CheckIcon } from '@heroicons/vue/outline'
import TestCardDetails from '@/Components/Payments/TestCardDetails'

import { useErrorsStore } from '@/Stores/errors'
const errors = useErrorsStore()
import { useAuthStore } from '@/Stores/auth'
const auth = useAuthStore()
import { usePaymentsStore } from '@/Stores/payments'
const payments = usePaymentsStore()

import { ref, onMounted } from 'vue'

const props = defineProps({
    amount: {
        type: Number,
        default: null
    }
});

const emit = defineEmits([
	'token',
	'card-saved',
])

/**
 * This component is based on the payway documentation SPA example here: https://www.payway.com.au/docs/rest.html#trusted-frame-spa
 */
const saveCardCheckbox = ref(false)
const autoCardCheckbox = ref(false)
const creditCardFrame = ref(null)
const publishableKey = process.env.MIX_PAYWAY_PUBLISHABLE_KEY
const submittingCardForm = ref(false)

// when the component mounts, load up the payway iframe
onMounted(() => {
    createCreditCardFrame()
})

const createCreditCardFrame = function() {

	// check for the existence of a #payway-credit-card before we try to create the iframe
	if( !document.querySelector('#payway-credit-card') ) {
		return console.error('No #payway-credit-card element found - cannot create payway iframe')
	}
	
    payway.createCreditCardFrame({    
        publishableApiKey: publishableKey,
        tokenMode: "callback",
        // onValid: function() { payButton.disabled = false },
        // onInvalid: function() { payButton.disabled = true }
    }, createdCallback )
}

const recreateCreditCardFrame = function(){
    creditCardFrame.value.destroy()
    creditCardFrame.value = null
    createCreditCardFrame()
}

// this function fires off once the iframe is loaded
const createdCallback = function( err, frame ) {
    if ( err ) {
        errors.push({
			type: 'error',
            title: "Couldn't create the payment form!",
            message: err.message,
        })
    } else {
        // Save the created frame for when we get the token
        creditCardFrame.value = frame
    }
}

// return this as a promise so our promise button can wait for it in a parent component
function submitCardForm(){
	return new Promise((resolve, reject) => {
		submittingCardForm.value = true

		// the arrow function here is a callback that is fired off 
		// once the card tokenization request returns from payway
		creditCardFrame.value.getToken(( err, data ) => {
			if ( err ) { 
				// there has been an error from payway
				errors.push({
					type: 'error',
					title: "Couldn't retrieve a payment token!",
					message: err.message,
				})
				reject(err)
			} else { 
				// card has been tokenized, so we can now send the token to our server
				saveToken( data ).then(response => {
					resolve(response)
				}).catch(error => {
					reject(error)
				})
			}
		})
	}).finally(() => {
		// reset the form
		recreateCreditCardFrame()
		submittingCardForm.value = false
	})
}
// expose the above promise so we can call it from a parent component
defineExpose({ submitCardForm: () => submitCardForm() });

// once we have a token from payway, send it to our server and save only - don't charge
const saveToken = function( data ) {
    return axios.post('/payway/store-token', {
        token: data.singleUseTokenId,
        cardable_id: payments.cardable_id,
        cardable_type: payments.cardable_type,
		save: true,
		auto: autoCardCheckbox.value,
		charge: false,
	})
    .then(response => {
        errors.push({
			type: 'success',
            title: "Card saved",
            message: "You can now use this payment method to make payments.",
        })
		window.setTimeout(() => {
			emit('card-saved')
		}, 1000)
		payments.runSuccessCallback()
	})
    .catch(error => {
        errors.push({
			type: 'error',
            title: "Couldn't store a payment token!",
            message: error.response.data.message,
        })
    })
}

</script>

<template>
    <div class="flex flex-col">
		
        <div id="payway-credit-card"></div>

		<component
			v-if="payments.infoComponent"
			:is="payments.infoComponent"
		/>
        
        <div class="flex self-end mt-3">
            <Label target="save_card">Use this card for automatic payments</Label>
            <input 
                type="checkbox"
                class="ml-2 text-base border font-light rounded px-2 py-2 mb-3 align-top focus:border-Blue focus:ring-blue-800 text-blue-800"
                v-model="autoCardCheckbox"
                id="auto_card"
            />
        </div>

		<!--TestCardDetails /-->

    </div>
</template>